import {MissingSentryProjectObservabilityError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import observabilityConfig from 'src/internal/config';
import * as reportsServer from 'src/internal/errors/reports.server';
import buildErrorScope from 'src/internal/errors/utils/buildErrorScope';
import generateId from 'src/internal/errors/utils/generateId';
import withSdkSignature from 'src/internal/errors/utils/withSdkSignature';

import type {ErrorScope, IReports, Severity} from 'src/internal/errors/types';
import type {ObservabilityConfig} from 'src/types';

const getInitConfig = (): ObservabilityConfig | undefined => {
  // If the config is not initialized, we don't want to log an error.
  return observabilityConfig.isInitialized()
    ? observabilityConfig.getAll()
    : undefined;
};

export class Reports implements IReports {
  #id;
  #config: ObservabilityConfig;

  constructor(config: ObservabilityConfig) {
    this.#config = {...getInitConfig(), ...config};
    this.#id = generateId(this.#config);

    if (
      config.errors?._internal_injected_methods_only ||
      config.errors?.disabled
    ) {
      return;
    }

    if (!this.#config.errors?.projects?.[this.#config.project]) {
      logErrorInDev(
        new MissingSentryProjectObservabilityError(this.#config.project),
      );
    }

    try {
      reportsServer.initReports(this.#id, config);
    } catch (error) {
      // The errors here are unexpected. But if they happen, degrade gracefully:
      // - Don't crash the code calling @sail/observability APIs
      // - Report an unhandled exception so that it is reported to Sentry
      Promise.reject(error);
    }
  }

  public error = (error: any, scope?: ErrorScope): Promise<void> => {
    return this.report('error', error, scope);
  };

  public warning = (error: any, scope?: ErrorScope): Promise<void> => {
    return this.report('warning', error, scope);
  };

  private async report(
    level: Exclude<Severity, 'info'>,
    error: any,
    scope?: ErrorScope,
  ): Promise<void> {
    logErrorInDev(error, level, scope);

    if (this.#config.errors?.disabled) {
      return;
    }

    if (scope?.project && !this.#config.errors?.projects?.[scope.project]) {
      logErrorInDev(new MissingSentryProjectObservabilityError(scope.project));
    }

    const injectedMethod =
      this.#config._internal_observability?.errors?.[level];

    if (injectedMethod) {
      const errorScope = buildErrorScope(this.#config, error, scope);

      injectedMethod(error, withSdkSignature(errorScope));
      return;
    }

    return reportsServer.report(
      level,
      this.#id,
      error,
      ...[scope].filter(Boolean),
    );
  }
}
